import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { CaregiverSummary, Colf, ModalService } from 'npx-family-happy-common';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  catchError,
  combineLatest,
  debounceTime,
  takeUntil,
} from 'rxjs';
import { AuthService } from 'src/app/authentication/auth/auth.service';
import {
  ColfState,
  RouterStateManagerService,
} from 'src/app/shared/services/router-state-manager/router-state-manager.service';
import { ColfService } from '../colf-service/colf-service.service';
import { ProvinceService } from 'src/app/shared/services/province-service/province.service';
import { GoogleMapsService } from 'src/app/requests-handling/caregiver-scoreboard/caregiver-selection/caregiver-selection-service/google-maps.service';
import {
  ModalDismissReasons,
  NgbModal,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { CaregiverSelectionRecordSortable } from 'src/app/shared/models/caregiverSelectionRecordSortable.models';
import { ColfFilterModalComponent } from '../colf-filter-modal/colf-filter-modal.component';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { InputMapComponent } from 'src/app/shared/components/input-map/input-map.component';
import { MapComponent } from 'src/app/shared/components/map/map/map.component';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { ToastComponent } from 'src/app/shared/components/toast/toast.component';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import { cellFormatter, emailFormatter } from 'src/app/shared/utils.functions';

@Component({
  selector: 'app-colf-list',
  templateUrl: './colf-list.component.html',
  styleUrls: ['./colf-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputMapComponent,
    MapComponent,
    RouterModule,
    NgbTooltipModule,
    AlertComponent,
    ColfFilterModalComponent,
    ToastComponent,
  ],
})
export class ColfListComponent implements OnInit, OnDestroy {
  toast!: ToastComponent | null;

  showAlert = false;
  showMore = false;
  showFilters = false;
  selectedRef = '';

  elementsPerPage = new UntypedFormControl(10);
  showPageButton = true;
  minAge!: number;
  maxAge!: number;
  private closeResult = '';

  form = new UntypedFormGroup({
    docRef: new UntypedFormControl(''),
    name: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    phone: new UntypedFormControl(''),
    fromAge: new UntypedFormControl(null),
    toAge: new UntypedFormControl(null),
    page: new UntypedFormControl(1),
    latitude: new UntypedFormControl(null),
    longitude: new UntypedFormControl(null),
    range: new UntypedFormControl(5),
    /* limit: new FormControl(10) */
  });
  filters: any = {}; //filters of Colf filter modal
  filtersObserver = new BehaviorSubject<any>(undefined);
  filtersRecreated = false;
  resetFiltersModal = false;
  colfs: CaregiverSummary[] = [];
  original: CaregiverSummary[] = [];
  private province = '';
  private subscription!: Subscription;
  private colfState!: ColfState;
  private unsubscribe = new Subject<void>();
  isAdmin = false;

  //MAP FILTERS
  sortableBB: CaregiverSelectionRecordSortable[] = [];
  address!: any;
  addressInput = new UntypedFormControl('');
  resetChildren = false;

  constructor(
    private colfService: ColfService,
    private provinceService: ProvinceService,
    private stateManager: RouterStateManagerService,
    private modalService: ModalService,
    private authService: AuthService,
    private googleMapsService: GoogleMapsService,
    private ngbModalService: NgbModal,
    private toastService: ToastService
  ) {
    this.authService
      .getToken()
      .subscribe(
        (token) => (this.isAdmin = this.authService.isUserAdmin(token) ?? false)
      );
    this.toastService
      .getChildComponent()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((component) => (this.toast = component));
  }

  ngOnInit(): void {
    let subscriptions: any = {};
    subscriptions.province = this.provinceService.getProvince().pipe(
      catchError((error) => {
        return error;
      })
    );
    subscriptions.state = this.stateManager.getColfState().pipe(
      catchError((error) => {
        return error;
      })
    );
    combineLatest(subscriptions)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((values: any) => {
        this.province = values.province;
        this.colfState = values.state;
        if (!this.colfState.filters.province) {
          this.colfState.filters.province = '';
        }
        this.colfState.filters.page = 1;
        if (Object.keys(this.colfState.filters).length > 0) {
          if (this.province !== this.colfState.filters.province) {
            this.filters = this.recreateFilterObject(
              this.colfState.filters,
              false
            );
          } else {
            this.filters = this.recreateFilterObject(
              this.colfState.filters,
              true
            );
          }
          this.colfState.filters.province;
          this.filtersObserver.next(this.filters);
          this.form.setValue(
            {
              docRef: this.colfState.filters.docRef ?? '',
              name: this.colfState.filters.info?.name ?? '',
              email: this.colfState.filters.info?.email ?? '',
              phone: this.colfState.filters.info?.phone ?? '',
              fromAge: this.colfState.filters.fromAge ?? null,
              toAge: this.colfState.filters.toAge ?? null,
              page: 1,
              latitude: this.colfState.filters.latitude ?? null,
              longitude: this.colfState.filters.longitude ?? null,
              range: this.colfState.filters.range ?? 10,
            },
            { emitEvent: true }
          );
          this.addressInput.setValue(this.colfState.filters.address ?? '');
        }
        if (
          Object.keys(this.colfState.filters).length === 0 &&
          this.subscription
        ) {
          try {
            this.askMoreColfs();
          } catch (err) {
            this.modalService.showErrorMessage(
              'Non è stato possibile caricare i nuovi dati aggiornati.'
            );
          }
        }
        if (!this.subscription) {
          if (this.colfState.filters.province === '') {
            this.colfState.filters.province = null;
          }
          this.subscription = this.colfService
            .getColf(this.colfState.filters, this.elementsPerPage.value)
            .subscribe((data) => {
              if (!this.filtersRecreated) {
                this.populateColf(data);
              } else {
                this.filtersRecreated = false;
              }
            });
          this.form.valueChanges
            .pipe(debounceTime(300), takeUntil(this.unsubscribe))
            .subscribe((value) => {
              if (value.page > (this.colfState.filters.page ?? 1)) {
                try {
                  this.askMoreColfs();
                } catch (err) {
                  this.modalService.showErrorMessage(
                    'Non è stato possibile caricare i nuovi dati aggiornati.'
                  );
                }
              } else {
                this.colfs = [];
                this.page?.setValue(1, { emitEvent: false });
                try {
                  this.askMoreColfs();
                } catch (err) {
                  this.modalService.showErrorMessage(
                    'Non è stato possibile caricare i nuovi dati aggiornati.'
                  );
                }
              }
            });
          this.elementsPerPage.valueChanges
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((value) => {
              this.colfs = [];
              this.colfService
                .getColf(this.colfState.filters, value)
                .subscribe();
            });
        }
      });
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this.subscription.unsubscribe();
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stateManager.setColfState(this.colfState, false);
  }

  generateCaregiverListSortable(bb: Colf[]) {
    this.sortableBB = [];
    bb.forEach((colf) => {
      let bbSortable = {
        fittingIndex: 0,
        record: {
          id: 0,
          requestID: 0,
          babysitter: null,
          petsitter: null,
          nurse: null,
          colf: colf,
          nutritionist: null,
          psychologist: null,
          coach: null,
          suitable: false,
          suitableDate: null,
          selected: false,
          selectedDate: null,
          chosen: false,
          chosenDate: null,
          discarded: false,
          discardedDate: null,
          discardedReason: '',
          distance: 0,
          favorite: false,
          cvSent: false,
          cvSentDate: null,
          contactSent: false,
          contactSentDate: null,
        },
      };
      this.sortableBB.push(bbSortable);
    });
  }

  rowClick(Colf: any) {
    if (!Colf) {
      this.colfState.id = 'new';
    } else {
      this.colfState.id = Colf.docRef;
    }
    this.colfState.route = 'colf-detail/' + this.colfState.id;
    this.colfState.step = 0;
    this.colfState.colf = undefined;
    this.ngOnDestroy();
    this.stateManager.setColfState(this.colfState);
  }

  /* Delete nurse code */
  deleteColf(docRef: string) {
    this.selectedRef = docRef;
    this.showAlert = true;
  }

  onAlertConfimation(event: boolean) {
    if (event) {
      const index = this.colfs.findIndex(
        (item) => item.docRef === this.selectedRef
      );
      this.colfs.splice(index, 1);
      this.original.splice(index, 1);
      this.colfService
        .deleteColf(this.selectedRef)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res: any) => {
          this.modalService.outcomeMessage.next('Colf eliminata con successo.');
          this.modalService.outcomeType.next('Success');
          this.modalService.showOutcome.next(true);
        });
    }
    this.selectedRef = '';
    this.showAlert = false;
  }

  /* Filter management code */
  showFiltersModal() {
    this.showFilters = true;
    this.filtersObserver.next(this.filters);
  }

  resetFilters() {
    this.colfs = [];
    this.resetFiltersModal = true;
    this.resetChildren = true;
    this.form.reset(
      {
        docRef: '',
        name: '',
        email: '',
        phone: '',
        fromAge: null,
        toAge: null,
        page: 1,
      },
      { emitEvent: false }
    );
    // Reset nurse filter modal filters
    this.filters = undefined;
    this.filtersObserver.next(this.filters);
    this.askMoreColfs(true);
  }

  processFilters(reset = false) {
    this.showFilters = false;
    let filters: any = {};
    if (reset) {
      filters = { page: 1 }; // Load all the Colfs
    } else {
      filters.info = {};
      filters.avail = {};
      filters.exp = {};
      filters.lang = {};
      filters.rating = {};
      filters.engagement = {};
      filters.cert = {};
      filters.page = this.page?.value;
      if (this.docRef?.value !== '') {
        filters.docRef = this.docRef?.value;
      }
      if (this.name?.value !== '') {
        if (filters.info) {
          filters.info.name = this.name?.value;
        }
      }
      if (this.email?.value !== '') {
        if (filters.info) {
          let formattedMail = emailFormatter(this.email?.value);
          filters.info.email = formattedMail;
        }
      }
      if (this.phone?.value !== '') {
        if (filters.info) {
          let formattedPhone = cellFormatter(this.phone?.value);
          filters.info.phone = formattedPhone;
        }
      }
      if (this.fromAge?.value || this.toAge?.value) {
        filters.fromAge = this.fromAge?.value;
        filters.toAge = this.toAge?.value;
      }
      if (this.latitude !== null) {
        filters.latitude = this.latitude.value;
      }
      if (this.longitude !== null) {
        filters.longitude = this.longitude.value;
      }
      if (this.range !== null) {
        filters.range = this.range.value;
      }
      if (this.addressInput !== null) {
        filters.address = this.addressInput.value;
      }
      if (this.filters) {
        if (this.filters.roles.cooking) {
          filters.avail.cooking = true;
        }
        if (this.filters.roles.gardening) {
          filters.avail.gardening = true;
        }
        if (this.filters.roles.groceries) {
          filters.avail.groceries = true;
        }
        if (this.filters.roles.helping) {
          filters.avail.helping = true;
        }
        if (this.filters.roles.houseCleaning) {
          filters.avail.houseCleaning = true;
        }
        if (this.filters.roles.ironing) {
          filters.avail.ironing = true;
        }
        if (this.filters.roles.sewing) {
          filters.avail.sewing = true;
        }
        if (this.filters.roles.terraceCleaning) {
          filters.avail.terraceCleaning = true;
        }
        if (this.filters.roles.washingMachines) {
          filters.avail.washingMachines = true;
        }
        if (this.filters.roles.windowCleaning) {
          filters.avail.windowCleaning = true;
        }
        // if (this.filters.specialities) {
        //   filters.lang.specialities = this.filters.specialities;
        // }
        if (this.filters.rating) {
          filters.rating.overall = true;
        }
        if (this.filters.fhsub) {
          filters.info.fhsub = true;
          filters.info.fhSubManual = true;
        }
        if (this.filters.license) {
          filters.avail.license = true;
        }
        if (this.filters.car) {
          filters.avail.car = true;
        }
        const days = this.colfService.fromAvailObjectToString(
          this.filters?.days,
          'days'
        );
        if (days !== '') {
          filters.avail.days = days;
        }
        const hours = this.colfService.fromAvailObjectToString(
          this.filters?.hours,
          'hours'
        );
        if (hours !== '') {
          filters.avail.hours = hours;
        }
        const contract = this.colfService.fromAvailObjectToString(
          this.filters?.contract,
          'contract'
        );
        if (contract !== '') {
          filters.avail.contract = contract;
        }
        if (this.filters.lang) {
          if (this.filters.lang.english) {
            filters.lang.english = true;
          }
          if (this.filters.lang.italian) {
            filters.lang.italian = true;
          }
          if (this.filters.lang.spanish) {
            filters.lang.spanish = true;
          }
          if (this.filters.lang.englishAdvanced) {
            filters.lang.englishAdvanced = true;
          }
          if (this.filters.lang.italianAdvanced) {
            filters.lang.italianAdvanced = true;
          }
          if (this.filters.lang.spanishAdvanced) {
            filters.lang.spanishAdvanced = true;
          }
        }
        if (this.filters.exp) {
          if (this.filters.exp.cluster1) {
            filters.exp.cluster1 = true;
          }
          if (this.filters.exp.cluster2) {
            filters.exp.cluster2 = true;
          }
        }
      }
    }
    if (this.province !== '') {
      if (!filters || Object.keys(filters).length === 0) {
        filters = { province: this.province };
      } else {
        filters.province = this.province;
      }
    }
    return filters;
  }

  recreateFilterObject(filters: any, fromRequest: boolean) {
    if (filters) {
      if (fromRequest) {
        this.filtersRecreated = true;
      }
      return {
        roles: {
          cooking: filters.roles ? filters.roles.cooking === true : false,
          gardening: filters.roles ? filters.roles.gardening === true : false,
          groceries: filters.roles ? filters.roles.groceries === true : false,
          helping: filters.roles ? filters.roles.helping === true : false,
          houseCleaning: filters.roles
            ? filters.roles.houseCleaning === true
            : false,
          ironing: filters.roles ? filters.roles.ironing === true : false,
          sewing: filters.roles ? filters.roles.sewing === true : false,
          terraceCleaning: filters.roles
            ? filters.roles.terraceCleaning === true
            : false,
          washingMachines: filters.roles
            ? filters.roles.washingMachines === true
            : false,
          windowCleaning: filters.roles
            ? filters.roles.windowCleaning === true
            : false,
        },
        page: filters.page ?? 1,
        minAge: this.fromAge?.value ?? null,
        maxAge: this.toAge?.value ?? null,
        rating: filters.rating ? filters.rating.overall === true : false,
        license: filters.avail ? filters.avail.license === true : false,
        car: filters.avail ? filters.avail.car === true : false,
        fhsub: filters.info ? filters.info.fhsub === true : false,
        days: this.colfService.fromStringToAvailObject(
          filters.avail ? (filters.avail.days ? filters.avail.days : '') : '',
          'days'
        ),
        hours: this.colfService.fromStringToAvailObject(
          filters.avail ? (filters.avail.hours ? filters.avail.hours : '') : '',
          'hours'
        ),
        contract: this.colfService.fromStringToAvailObject(
          filters.avail
            ? filters.avail.contract
              ? filters.avail.contract
              : ''
            : '',
          'contract'
        ),
        lang: {
          english: filters.lang ? filters.lang.english === true : false,
          englishAdvanced: filters.lang
            ? filters.lang.englishAdvanced === true
            : false,
          italian: filters.lang ? filters.lang.italian === true : false,
          italianAdvanced: filters.lang
            ? filters.lang.italianAdvanced === true
            : false,
          spanish: filters.lang ? filters.lang.spanish === true : false,
          spanishAdvanced: filters.lang
            ? filters.lang.spanishAdvanced === true
            : false,
        },
        exp: {
          cluster1: filters.exp ? filters.exp.cluster1 === true : false,
          cluster2: filters.exp ? filters.exp.cluster2 === true : false,
        },
      };
    }
    return undefined;
  }

  askMoreColfs(resetFilters = false) {
    const filters = this.processFilters(resetFilters);
    try {
      this.colfState.filters = filters;
      this.colfService
        .getColf(filters, this.elementsPerPage.value)
        .subscribe((colfs) => {
          if (colfs.length < this.elementsPerPage.value) {
            this.showPageButton = false;
          } else {
            this.showPageButton = true;
          }
        });
    } catch (err) {
      this.modalService.showErrorMessage(
        'Non è stato possibile caricare i nuovi dati aggiornati.'
      );
    }
  }

  get docRef() {
    return this.form.get('docRef');
  }

  get name() {
    return this.form.get('name');
  }

  get email() {
    return this.form.get('email');
  }

  get phone() {
    return this.form.get('phone');
  }

  get fromAge() {
    return this.form.get('fromAge');
  }

  get toAge() {
    return this.form.get('toAge');
  }

  get page() {
    return this.form.get('page');
  }
  get latitude() {
    return this.form.get('latitude');
  }
  get longitude() {
    return this.form.get('longitude');
  }
  get range() {
    return this.form.get('range');
  }

  private populateColf(array: Colf[]) {
    if (array.length > 0) {
      this.generateCaregiverListSortable(array);
      this.original = array.map((item) => {
        return {
          name: item.info ? item.info.name : '-',
          surname: item.info ? item.info.surname : '-',
          email: item.info ? item.info.email : '-',
          phone: item.info ? item.info.phone : '-',
          docRef: item.docRef,
          cv: item.info.resumeHidden ?? '-',
          video: item.info.video,
          dangerous: item.rating.dangerous,
          creationDate: new Date(),
          rating: item.rating.overall,
          subscription: item.info.subscription,
          manualSubscription: item.info.manualSubscription,
          certificate: item.certificate,
        };
      });
    } else {
      this.generateCaregiverListSortable(array);
      this.original = [];
    }
    //SET COORDINATES PERSISTENCE
    let mapInfo = {
      latitude: this.colfState.filters.latitude,
      longitude: this.colfState.filters.longitude,
      range: this.colfState.filters.range,
      addressName: this.colfState.filters.address,
      caregiverList: this.sortableBB,
    };
    this.googleMapsService.setCoordinates(mapInfo);
    this.colfs.push(...this.original);
  }

  loadMore() {
    this.page?.setValue(this.page.value + 1);
  }

  exportColfs() {
    const requestFilters = this.processFilters();
    try {
      this.colfService.exportColfsFromDB(requestFilters).subscribe((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'colfs.xlsx';
        link.click();
      });
    } catch (err) {
      this.modalService.showErrorMessage(
        `Non è stato possibile completare l'export dei dati, perché mancano non è stato possibile applicare i filtri.`
      );
    }
  }

  filterModalClose() {
    this.showFilters = false;
    this.resetChildren = false;
  }

  copyToClipboard(event: any) {
    navigator.clipboard.writeText(event);
    this.toastService.setAction(
      'Dati copiati con successo! Ora puoi incollarli dove vuoi.',
      true
    );
  }

  //MAPS
  open(content: any) {
    this.ngbModalService
      .open(content, { ariaLabelledBy: 'modal-basic-title' })
      .result.then(
        (result) => {
          this.closeResult = `Closed with: ${result}`;
        },
        (reason) => {
          this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
        }
      );
  }

  private getDismissReason(reason: any): string {
    switch (reason) {
      case ModalDismissReasons.ESC:
        return 'by pressing ESC';
      case ModalDismissReasons.BACKDROP_CLICK:
        return 'by clicking on a backdrop';
      default:
        return `with: ${reason}`;
    }
  }

  //SCRIPT --- CONVERT ADDRESSES INTO COORDINATES
  private async convertAddress(array: Colf[]) {
    let counterTotal = 0;
    let counterConverted = 0;
    let counterNull = 0;
    for (let i = 0; i < array.length; i++) {
      counterTotal++;
      const NURSE = array[i];
      const address = NURSE.info.address;
      const latitude = NURSE.info.latitude;
      const longitude = NURSE.info.longitude;
      if (address !== null && address !== '') {
        if (latitude === null && longitude === null) {
          try {
            const res = await this.googleMapsService.getCoordinates(address);
            const lat = res[0].geometry.location.lat();
            const lng = res[0].geometry.location.lng();

            const updatedNURSE = {
              ...NURSE,
              info: {
                ...NURSE.info,
                latitude: lat,
                longitude: lng,
              },
            };
            await this.colfService
              .updateColf(updatedNURSE)
              .pipe(takeUntil(this.unsubscribe))
              .toPromise();
            counterConverted++;

            // delay for each interaction
            await this.delay(2000);
          } catch (err) {
            console.log(err);
          }
        } else {
          console.log('ALREADY COORDINATES', longitude, latitude);
          counterNull++;
        }
      } else {
        console.log('UPDATED BB ADDRESS NULL', address);
        counterNull++;
      }
    }
    console.log(`conversioni totali: ${counterTotal}`);
    console.log(`conversioni fallite (o già con coordinate): ${counterNull}`);
    console.log(`conversioni riuscite: ${counterConverted}`);
  }

  async sendCoordinates(address: any) {
    try {
      if (address !== null && address !== '') {
        const res = await this.googleMapsService.getCoordinates(address);
        let coordinates = {
          lat: res[0].geometry.location.lat(),
          lng: res[0].geometry.location.lng(),
        };
        this.form.get('latitude')?.setValue(coordinates.lat);
        this.form.get('longitude')?.setValue(coordinates.lng);
        this.address = {
          latitude: this.form.get('latitude')?.value,
          longitude: this.form.get('longitude')?.value,
          range: this.form.get('range')?.value,
          addressName: address,
        };
      } else {
        console.log('insert address');
      }
    } catch {
      console.log('error');
    }
  }

  private delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  sendAddress(event: any) {
    this.resetChildren = false;
    this.addressInput.setValue(event);
    this.sendCoordinates(event);
  }
}
